import {
  CalendarStarIcon,
  HeadsetIcon,
  QuestionIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
} from '@phosphor-icons/react';
import { useState } from 'react';

import { ActionMenu, type ActionMenuItem } from '@/components/common/action-menu';
import { isFeatureEnabled } from '@/config/preview-features';
import { AddParametersModal, useEligibleDevices } from '@/features/device-params';
import {
  useOrderReplacementRequests,
  useOrderWarrantyClaims,
  type OrderDetail,
} from '@/features/orders';

import { ReplacementRequestsDrawer } from '../replacements/components/replacement-requests-drawer';
import { RequestReplacementDrawer } from '../replacements/components/request-replacement-drawer';
import { ClaimWarrantyDrawer } from '../warranty/components/claim-warranty-drawer';
import { WarrantyClaimsDrawer } from '../warranty/components/warranty-claims-drawer';

import { RaiseTicketDrawer } from './raise-ticket-drawer';
import { ReviewsDrawer } from './reviews-drawer';

type Overlay =
  | 'ticket'
  | 'review'
  | 'replacement-intake'
  | 'replacement-list'
  | 'warranty-intake'
  | 'warranty-list'
  | 'params'
  | null;

export function OrderActionsMenu({ order }: { order: OrderDetail }) {
  const [overlay, setOverlay] = useState<Overlay>(null);
  const close = () => setOverlay(null);
  const isDelivered = order.status === 'delivered';
  // Mount-time on delivered orders, so the click below is a synchronous read (ADR-0022).
  const { data: replacements } = useOrderReplacementRequests(order.id, isDelivered);
  // Parallel, never chained (ADR-0023) — same `enabled`, issued alongside the query above, so the
  // click below is a synchronous read of a warm cache. One query drives routing here, and the rows
  // and the Raise Warranty Claim CTA inside the drawer.
  const { data: warranty } = useOrderWarrantyClaims(order.id, isDelivered);
  // Unconditional, unlike the two queries above: gating this fetch on order status would itself be
  // frontend eligibility logic. The backend applies all five predicates and answers `[]` when there
  // is nothing to configure, so a cancelled or fully-configured order hides the item on its own
  // (ADR-0024).
  const orderScope = { scope: 'order' as const, orderId: order.id, customerId: order.customer.id };
  // DM Configuration is outside the release scope (ADR-0034): the eligibility question is not even
  // asked, so the menu item never appears.
  const paramsEnabled = isFeatureEnabled('dm-configuration');
  const { data: eligibleDevices } = useEligibleDevices(orderScope, { enabled: paramsEnabled });

  // Raise a Ticket is withheld from the release scope (ADR-0034): `GET /orders/admin/meta/assignees`
  // and `POST /orders/admin/:id/tickets` do not exist on the backend yet (ADO 120855). Only the
  // entry point is gated; the hook, drawer and MSW handlers are ahead of their endpoints, not wrong.
  const ticketsEnabled = isFeatureEnabled('support');

  const items: ActionMenuItem[] = [];
  if (ticketsEnabled) {
    items.push({
      key: 'ticket',
      label: 'Raise a Ticket',
      icon: <HeadsetIcon size={16} />,
      onClick: () => setOverlay('ticket'),
    });
  }
  // Delivered alone gates the entry point (AC 1a/1b/1c): it is listed whether or not the Customer
  // has submitted anything, since 1c routes an unrated Delivered order to the panel's empty state.
  // No feature key — unlike Raise a Ticket above, Reviews is inside the release scope, so ADR-0034
  // gives it none (`PREVIEW_FEATURES` gains no `order-reviews` entry). `order.hasReview`, the old
  // gate, is gone with the contract rebuild below this commit.
  if (isDelivered) {
    items.push({
      key: 'review',
      label: 'Read Review',
      icon: <CalendarStarIcon size={16} />,
      onClick: () => setOverlay('review'),
    });
  }
  // Listed for every delivered order and never hidden: the replacement window governs where the
  // item goes and whether a new request can be raised, not whether it appears (AC 112365).
  if (isDelivered) {
    items.push({
      key: 'replacement',
      label: 'Replacement Request',
      icon: <QuestionIcon size={16} />,
      // Straight to intake only in the common case; every other combination — including a click
      // before the query resolves — opens the listing, which then shows its own loading state.
      onClick: () =>
        setOverlay(
          replacements && replacements.isWithinWindow && replacements.requests.length === 0
            ? 'replacement-intake'
            : 'replacement-list',
        ),
    });
  }
  // Warranty takes over exactly as the replacement window closes, so the two items are never both
  // actionable. `isWithinWindow` is the backend's boundary, read off the query above rather than
  // recomputed — the frontend holds no opinion about the day count (ADR-0023). `=== false` not `!`:
  // while the query is unresolved the item stays hidden, because appearing early would offer
  // warranty *during* the replacement window (AC 112444).
  if (isDelivered && replacements?.isWithinWindow === false) {
    items.push({
      key: 'warranty',
      label: 'Claim Warranty',
      icon: <ShieldCheckIcon size={16} />,
      // Straight to intake only when there is no history worth showing first *and* something is
      // actually claimable; every other combination — including a click before the query resolves —
      // opens the listing, which then shows its own loading, view-only or empty state (ADR-0023).
      onClick: () =>
        setOverlay(
          warranty && warranty.hasEligibleDevices && warranty.claims.length === 0
            ? 'warranty-intake'
            : 'warranty-list',
        ),
    });
  }
  // `&& length > 0`, not `?.length`: while the query is unresolved the item stays hidden, because
  // offering the flow with an empty device dropdown is worse than offering it a moment late.
  if (eligibleDevices && eligibleDevices.length > 0) {
    items.push({
      key: 'params',
      // "Add Parameter Template" on all three entry points, overriding the AC's "Set Parameter
      // Template" so the menu item cannot read differently from the row link beneath it
      // (grilling Q8, FLAG-BA-2).
      label: 'Add Parameter Template',
      icon: <SlidersHorizontalIcon size={16} />,
      onClick: () => setOverlay('params'),
    });
  }

  return (
    <>
      <ActionMenu triggerLabel="Actions" groups={[{ items }]} horizontal />
      {ticketsEnabled ? (
        <RaiseTicketDrawer order={order} open={overlay === 'ticket'} onClose={close} />
      ) : null}
      <ReviewsDrawer order={order} open={overlay === 'review'} onClose={close} />
      <RequestReplacementDrawer
        orderId={order.id}
        customerId={order.customer.id}
        open={overlay === 'replacement-intake'}
        onClose={close}
      />
      <ReplacementRequestsDrawer
        order={order}
        open={overlay === 'replacement-list'}
        onClose={close}
      />
      <ClaimWarrantyDrawer
        orderId={order.id}
        customerId={order.customer.id}
        open={overlay === 'warranty-intake'}
        onClose={close}
      />
      <WarrantyClaimsDrawer order={order} open={overlay === 'warranty-list'} onClose={close} />
      {paramsEnabled ? (
        <AddParametersModal scope={orderScope} open={overlay === 'params'} onClose={close} />
      ) : null}
    </>
  );
}
